import moment from "moment";
import { CHANGE_ATTRIBUTE_NAME_MAP, NO_RECENT_LEAVE_CHANGES_MESSAGE, noDataPlaceholder } from "./constants.js";
import { PREVIOUS_DAY_SUBVIEWS, PREVIOUS_DAY_PERIODS } from "./ExcelConstants.js";
export const EXCEL_DECIMAL_FORMAT = "0.00";
export const isDecimalExportColumn = (column = {}) => typeof column.sortingMethod === "string" &&
    column.sortingMethod.trim().toLowerCase() === "decimal";
export const resolveExcelExportNumericValue = (value) => {
    if (typeof value === "number") {
        return Number.isFinite(value) ? { parsedValue: value } : null;
    }
    if (typeof value !== "string") {
        return null;
    }
    const trimmedValue = value.trim();
    if (!trimmedValue || trimmedValue.toLowerCase() === "no access") {
        return null;
    }
    const cleanedValue = trimmedValue.replace(/[$,\s]/g, "");
    if (!/^-?\d*\.?\d+$/.test(cleanedValue)) {
        return null;
    }
    const parsedValue = Number(cleanedValue);
    return Number.isFinite(parsedValue) ? { parsedValue } : null;
};
export const shouldUsePreviousDay = (subViewName, periodName) => PREVIOUS_DAY_SUBVIEWS.includes(subViewName) &&
    PREVIOUS_DAY_PERIODS.includes(periodName);
export const RemoveYear = (dateobject) => {
    if (dateobject === undefined || dateobject === "" || dateobject === null) {
        return null;
    }
    const dateStr = typeof dateobject === "number" ? String(dateobject) : dateobject;
    return dateStr.includes("2099") ? noDataPlaceholder : dateStr;
};
const IN_ERROR_REASON_REGEX = /(?:\bmuerr\b|\bmuper\b|in[-\s]*error|up[-\s]*in[-\s]*error)/i;
export const isInErrorReason = (value) => {
    if (value === null || value === undefined)
        return false;
    if (Array.isArray(value)) {
        return value.some(isInErrorReason);
    }
    const text = String(value).trim();
    if (!text)
        return false;
    return IN_ERROR_REASON_REGEX.test(text);
};
export const hasInErrorReason = (...values) => values.some((value) => isInErrorReason(value));
const toNonNegativeNumber = (value) => {
    if (typeof value !== "number" && typeof value !== "string") {
        return 0;
    }
    const number = Number.parseFloat(String(value));
    return Number.isFinite(number) ? Math.max(number, 0) : 0;
};
const AS_NEEDED_LABEL = "As Needed";
// NL "As Needed" indicators arrive as "Y"/"N" strings.
const isIndicatorEnabled = (value) => typeof value === "string" && value.trim().toLowerCase() === "y";
export const formatTreatmentDetails = (treatmentDetails, leaveSourceSystem) => {
    if (!treatmentDetails) {
        return null;
    }
    const scheduleTypeDesc = treatmentDetails?.scheduleTypeDesc?.trim();
    const simpleScheduleTypes = ["As Needed", "Daily", "None", "Other", "Unknown"];
    if (scheduleTypeDesc && simpleScheduleTypes.includes(scheduleTypeDesc)) {
        return scheduleTypeDesc;
    }
    const isFineosOfficeVisit = leaveSourceSystem === "FNEOS" && scheduleTypeDesc === "Office Visit Episodic";
    const isNavilinkTreatment = leaveSourceSystem === "NVLNK" && scheduleTypeDesc === "Specific Treatment Frequency";
    if (!isFineosOfficeVisit && !isNavilinkTreatment) {
        return null;
    }
    // "Not Applicable/No Recovery" wins over "As Needed" on the recovery row.
    const recoveryDurationAsNeeded = isIndicatorEnabled(treatmentDetails.recoveryDurationAsOfNeedInd) &&
        !isIndicatorEnabled(treatmentDetails.nbrRecoveryInd);
    const numberOfTreatmentsAsNeeded = isIndicatorEnabled(treatmentDetails.treatmentAsOfNeedInd);
    // Treatment Period (recoveryDuration* fields) is always required; it never shows "As Needed".
    if (!treatmentDetails.recoveryDurationUnitTypeDesc?.trim()) {
        return null;
    }
    // Treatment Recovery Duration unit is required only when that slot isn't shown as "As Needed".
    if (!recoveryDurationAsNeeded && !treatmentDetails.periodUnitTypeDesc?.trim()) {
        return null;
    }
    const treatmentPeriod = "every " +
        toNonNegativeNumber(treatmentDetails.recoveryDurationQty) +
        " " +
        treatmentDetails.recoveryDurationUnitTypeDesc;
    if (recoveryDurationAsNeeded && numberOfTreatmentsAsNeeded) {
        return AS_NEEDED_LABEL + " " + treatmentPeriod;
    }
    const treatmentRecoveryDurationSlot = recoveryDurationAsNeeded
        ? AS_NEEDED_LABEL
        : toNonNegativeNumber(treatmentDetails.periodQty) + " " + treatmentDetails.periodUnitTypeDesc;
    const numberOfTreatmentsSlot = numberOfTreatmentsAsNeeded
        ? AS_NEEDED_LABEL
        : toNonNegativeNumber(treatmentDetails.treatmentQty) + " times";
    return treatmentRecoveryDurationSlot + ", " + numberOfTreatmentsSlot + " " + treatmentPeriod;
};
export const formatEpisodeDetails = (episodeDetails, leaveSourceSystem) => {
    if (!episodeDetails) {
        return null;
    }
    const scheduleTypeDesc = episodeDetails?.scheduleTypeDesc?.trim();
    const simpleScheduleTypes = ["As Needed", "Daily", "None", "Other", "Unknown"];
    if (scheduleTypeDesc && simpleScheduleTypes.includes(scheduleTypeDesc)) {
        return scheduleTypeDesc;
    }
    const isFineosEpisodic = leaveSourceSystem === "FNEOS" &&
        (scheduleTypeDesc === "Incapacity Episodic" || scheduleTypeDesc === "Episodic");
    const isNavilinkEpisodic = leaveSourceSystem === "NVLNK" && scheduleTypeDesc === "Specific Episode Frequency";
    if (!isFineosEpisodic && !isNavilinkEpisodic) {
        return null;
    }
    const episodeDurationAsNeeded = isIndicatorEnabled(episodeDetails.episodeDurationAsOfNeedInd);
    const numberOfEpisodesAsNeeded = isIndicatorEnabled(episodeDetails.episodeAsOfNeedInd);
    // Episode Period (recoveryDuration* fields) is always required; it never shows "As Needed".
    if (!episodeDetails.recoveryDurationUnitTypeDesc?.trim()) {
        return null;
    }
    // Episode Duration unit is required only when that slot isn't shown as "As Needed".
    if (!episodeDurationAsNeeded && !episodeDetails.periodUnitTypeDesc?.trim()) {
        return null;
    }
    const episodePeriod = "every " +
        toNonNegativeNumber(episodeDetails.recoveryDurationQty) +
        " " +
        episodeDetails.recoveryDurationUnitTypeDesc;
    if (episodeDurationAsNeeded && numberOfEpisodesAsNeeded) {
        return AS_NEEDED_LABEL + " " + episodePeriod;
    }
    const episodeDurationSlot = episodeDurationAsNeeded
        ? AS_NEEDED_LABEL
        : toNonNegativeNumber(episodeDetails.periodQty) + " " + episodeDetails.periodUnitTypeDesc;
    const numberOfEpisodesSlot = numberOfEpisodesAsNeeded
        ? AS_NEEDED_LABEL
        : toNonNegativeNumber(episodeDetails.episodeQty) + " times";
    return episodeDurationSlot + ", " + numberOfEpisodesSlot + " " + episodePeriod;
};
const getStartOfToday = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
};
export const isActiveDigitalAddress = (digitalAddressEndDt, asOfDate = getStartOfToday()) => {
    if (!digitalAddressEndDt) {
        return true;
    }
    const parsedEndDate = new Date(digitalAddressEndDt);
    if (Number.isNaN(parsedEndDate.getTime())) {
        return false;
    }
    return parsedEndDate >= asOfDate;
};
export const getLatestChangeReason = (leave) => {
    const changeDate = leave.lastChangedAttribute?.changeDate;
    const changeAttribute = leave.lastChangedAttribute?.attributeName;
    if (changeDate && isDateWithinLast30Days(changeDate)) {
        return CHANGE_ATTRIBUTE_NAME_MAP[changeAttribute] ?? NO_RECENT_LEAVE_CHANGES_MESSAGE;
    }
    return NO_RECENT_LEAVE_CHANGES_MESSAGE;
};
export const getLatestChangeDate = (leave) => {
    const changeDate = leave.lastChangedAttribute?.changeDate;
    if (changeDate && isDateWithinLast30Days(changeDate)) {
        return changeDate;
    }
    return NO_RECENT_LEAVE_CHANGES_MESSAGE;
};
export const isDateWithinLast30Days = (dateString) => {
    try {
        const formats = ["MM/DD/YYYY"];
        const date = moment(dateString, formats, true);
        if (!date.isValid()) {
            return false;
        }
        const today = moment().startOf("day");
        const thirtyDaysAgo = today.clone().subtract(30, "days");
        return date.isBetween(thirtyDaysAgo, today, "day", "[]");
    }
    catch (e) {
        return false;
    }
};
